import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '../../i18n/I18nContext.jsx';

export function TopBar({
  searchQuery,
  onSearchChange,
  onLogout,
  onClockOut,
  children,
  showSearch = true,
  onToggleSidebar,
  isSidebarOpen = false,
  isClockedIn = false,
}) {
  const { t } = useI18n();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const handleLogout = () => {
    setShowUserMenu(false);
    onLogout?.();
  };

  const handleClockOutAndLogout = async () => {
    setShowUserMenu(false);
    if (onClockOut) {
      const success = await onClockOut();
      if (!success) {
        // Clock out failed, don't proceed with logout
        return;
      }
    }
    // Only logout if clock out succeeded (or if onClockOut is not provided)
    onLogout?.();
  };

  return (
    <header className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 border-b border-zinc-800 bg-[#121212]/80 backdrop-blur">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {onToggleSidebar && (
          <button
            type="button"
            onClick={onToggleSidebar}
            aria-label={isSidebarOpen ? 'Close navigation' : 'Open navigation'}
            className="shrink-0 p-2 rounded-xl border border-zinc-800 bg-zinc-900 text-zinc-300 hover:text-emerald-200 hover:border-emerald-primary transition-colors"
          >
            <span className="text-xl leading-none">{isSidebarOpen ? '✕' : '☰'}</span>
          </button>
        )}
        {showSearch ? (
          <div className="relative flex-1 min-w-0">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">🔍</span>
            <input
              type="search"
              placeholder={t('topBar.searchTables')}
              value={searchQuery}
              onChange={(e) => onSearchChange?.(e.target.value)}
              className="w-full rounded-2xl bg-zinc-900 border border-zinc-800 pl-10 pr-4 py-2 text-sm text-zinc-200 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-primary"
            />
          </div>
        ) : (
          <div className="flex-1" />
        )}
      </div>

      <div className="flex items-center gap-2 sm:gap-4 shrink-0">
        {children}
        <div className="relative" ref={userMenuRef}>
          <button
            type="button"
            onClick={() => setShowUserMenu((prev) => !prev)}
            className="flex items-center gap-1.5 sm:gap-2 rounded-2xl bg-zinc-900 px-3 sm:px-4 py-2 text-sm font-medium text-zinc-200 border border-zinc-800 hover:border-emerald-primary hover:text-emerald-200 transition"
          >
            <span className="text-lg">👤</span>
            <span className="hidden sm:inline">{t('topBar.account')}</span>
          </button>
          {showUserMenu && (
            <div className="absolute right-0 mt-2 mr-2 w-44 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl z-50 overflow-hidden">
              {isClockedIn && (
                <button
                  type="button"
                  onClick={handleClockOutAndLogout}
                  className="w-full px-2.5 py-1.5 text-left text-[11px] text-zinc-200 hover:bg-zinc-800 transition-colors flex items-center gap-1.5 border-b border-zinc-700 whitespace-nowrap"
                >
                  <span className="text-xs">⏰</span>
                  <span className="leading-tight">{t('topBar.clockOutAndLogOut')}</span>
                </button>
              )}
              <button
                type="button"
                onClick={handleLogout}
                className="w-full px-2.5 py-1.5 text-left text-[11px] text-zinc-200 hover:bg-zinc-800 transition-colors flex items-center gap-1.5"
              >
                <span className="text-xs">🚪</span>
                <span className="leading-tight">{t('topBar.logOut')}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

